/**
 * ヘッダーのお知らせバー用：「日付がまだ来ていない、いちばん近いお知らせ」を求める。
 *
 * 対象日の決め方（上から優先）
 *   1. お知らせに eventDate: 'YYYY-MM-DD'（または eventDates: [...]）があればその日付
 *   2. なければタイトル・本文中の「◯月◯日」「YYYY年◯月◯日」を読み取る
 *      （年の記載がなければ投稿日の年。投稿日より前になる場合は翌年とみなす）
 * 今日（日本時間）以降の日付を持つお知らせのうち、日付が最も近いものを返す。無ければ null。
 */
import { mergeStaticInformation } from '@/data/staticInformation'
import Global from '@/components/Global'

const WEEK = ['日', '月', '火', '水', '木', '金', '土']

// 全角数字 → 半角
const toHalf = s => String(s || '').replace(/[０-９]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xFEE0))

// 日本時間の「今日 0:00」を YYYYMMDD の数値で
function todayNum() {
  const d = new Date(Date.now() + (new Date().getTimezoneOffset() + 540) * 60000)
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate()
}

function parsePosted(createTime) {
  const m = toHalf(createTime).match(/(\d{4})\D(\d{1,2})\D(\d{1,2})/)
  return m ? { y: +m[1], n: +m[1] * 10000 + +m[2] * 100 + +m[3] } : null
}

function eventDatesOf(item) {
  const explicit = [].concat(item.eventDates || [], item.eventDate || [])
  if (explicit.length) {
    return explicit.map(s => {
      const m = toHalf(s).match(/(\d{4})\D(\d{1,2})\D(\d{1,2})/)
      return m && { y: +m[1], m: +m[2], d: +m[3] }
    }).filter(Boolean)
  }
  const posted = parsePosted(item.createTime)
  if (!posted) return []
  const text = toHalf(`${item.title || ''}\n${item.content || ''}`)
  const re = /(?:(\d{4})\s*年\s*)?(\d{1,2})\s*月\s*(\d{1,2})\s*日/g
  const out = []
  let m
  while ((m = re.exec(text))) {
    const mo = +m[2], d = +m[3]
    if (mo < 1 || mo > 12 || d < 1 || d > 31) continue
    let y = m[1] ? +m[1] : posted.y
    // 年の記載がなく、投稿日より前になる場合（12月投稿で「1月◯日」等）は翌年
    if (!m[1] && y * 10000 + mo * 100 + d < posted.n) y += 1
    out.push({ y, m: mo, d })
  }
  return out
}

export function pickUpcoming(list) {
  const today = todayNum()
  let best = null
  for (const item of list || []) {
    for (const e of eventDatesOf(item)) {
      const n = e.y * 10000 + e.m * 100 + e.d
      if (n >= today && (!best || n < best.n)) best = { n, e, item }
    }
  }
  if (!best) return null
  const { e, item } = best
  const w = WEEK[new Date(e.y, e.m - 1, e.d).getDay()]
  return {
    id: item.id,
    date: `${e.m}月${e.d}日（${w}）`,
    // 絵文字などの装飾を除いたタイトル
    title: String(item.title || '').replace(/[\u{1F300}-\u{1FAFF}☀-➿]/gu, '').trim(),
  }
}

// 首页のお知らせ欄に出す期間：本文に日付があればその日（最後の日）まで、日付が無ければ投稿から30日間
const SHOW_DAYS = 30

function plusDaysNum(n, days) {
  const d = new Date(Math.floor(n / 10000), Math.floor(n / 100) % 100 - 1, n % 100 + days)
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate()
}

/** 今も有効なお知らせか（期限が過ぎたものは首页に出さない） */
export function isCurrentNotice(item, today = todayNum()) {
  const dates = eventDatesOf(item).map(e => e.y * 10000 + e.m * 100 + e.d)
  if (dates.length) return Math.max(...dates) >= today
  const posted = parsePosted(item.createTime)
  return !!posted && plusDaysNum(posted.n, SHOW_DAYS) >= today
}

/** 有効なお知らせだけを新しい順に（limit 件まで） */
export function pickCurrent(list, limit) {
  const today = todayNum()
  const out = (list || []).filter(item => isCurrentNotice(item, today))
  return typeof limit === 'number' ? out.slice(0, limit) : out
}

// API は1回だけ取得して各コンポーネントで共有
let cache = null
export function loadUpcomingNotice() {
  if (!cache) {
    const staticOnly = () => pickUpcoming(mergeStaticInformation([]))
    cache = (window.axios
      ? window.axios.get(`${Global.host}/t/info/all`)
        .then(resp => pickUpcoming(mergeStaticInformation(resp.data)))
        .catch(staticOnly)
      : Promise.resolve(staticOnly()))
  }
  return cache
}
